import { useEffect, useState, type RefObject } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useNavigate } from 'react-router';
import { hitRegex } from './Mark';

/**
 * Wie vanuit de zoekresultaten een artikel opent, komt binnen met ?q=. Dit
 * onderdeel kleurt de zoekwoorden in de tekst en zet een klein balkje neer:
 * "n treffers voor 'kassa'", met vorige/volgende en een kruisje.
 *
 * Met de CSS Custom Highlight API en NIET door <mark>-elementen in de tekst
 * te prikken: de artikeltekst komt uit dangerouslySetInnerHTML, en wat je daar
 * in de DOM verandert raakt de inhoudsopgave, de lightbox en de kopieerknoppen
 * die diezelfde boom lezen. Highlights liggen erbóvenop en raken de boom niet.
 * Browsers zonder de API (Firefox < 140) tonen gewoon de tekst zonder kleur.
 *
 * De woorden komen uit dezelfde regel als de kaarten (hitRegex): aan het begin
 * van een woord, hoofdletter- en accentongevoelig.
 */
export function SearchTermHighlight({ q, scopeRef }: { q: string | null; scopeRef: RefObject<HTMLElement | null> }) {
  const navigate = useNavigate();
  const [treffers, setTreffers] = useState<Range[]>([]);
  const [actief, setActief] = useState(0);
  const ondersteund = typeof CSS !== 'undefined' && 'highlights' in CSS;

  useEffect(() => {
    if (!q || !ondersteund) { setTreffers([]); return; }
    const scope = scopeRef.current;
    if (!scope) return;
    const woorden = q.split(/\s+/).map((w) => w.trim()).filter((w) => w.length >= 3);
    const re = hitRegex(woorden);
    if (!re) { setTreffers([]); return; }

    const ranges: Range[] = [];
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const tekst = node.nodeValue || '';
      if (!tekst.trim()) continue;
      for (const m of tekst.matchAll(re)) {
        const r = new Range();
        r.setStart(node, m.index ?? 0);
        r.setEnd(node, (m.index ?? 0) + m[0].length);
        ranges.push(r);
      }
    }
    CSS.highlights.set('kb-zoekterm', new Highlight(...ranges));
    setTreffers(ranges);
    setActief(0);
    return () => {
      CSS.highlights.delete('kb-zoekterm');
      CSS.highlights.delete('kb-zoekterm-actief');
    };
  }, [q, scopeRef, ondersteund]);

  // De actieve treffer apart kleuren en in beeld brengen.
  useEffect(() => {
    if (!ondersteund || !treffers.length) return;
    const r = treffers[actief];
    if (!r) return;
    CSS.highlights.set('kb-zoekterm-actief', new Highlight(r));
    const rect = r.getBoundingClientRect();
    const buiten = rect.top < 96 || rect.bottom > window.innerHeight - 96;
    if (buiten) {
      const el = r.startContainer.parentElement;
      el?.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }, [actief, treffers, ondersteund]);

  if (!q || !ondersteund || treffers.length === 0) return null;

  const stap = (d: number) => setActief((i) => (i + d + treffers.length) % treffers.length);
  const sluit = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('q');
    navigate(url.pathname + url.search, { replace: true, preventScrollReset: true });
  };
  const knop = 'rounded-md p-1.5 text-ink-2 transition hover:bg-surface-hover hover:text-ink';

  return (
    <div
      role="status"
      /* Onderaan en niet bovenin: daar staan de broodkruimel en de leerpadrail.
         De variabele houdt hem boven de leerpadbalk als die er is. */
      className="kb-no-print fixed bottom-[calc(1.25rem+var(--kb-stepbar-h,0px))] left-1/2 z-90 flex -translate-x-1/2 items-center gap-1 rounded-full border border-hairline bg-raised py-1 pr-1 pl-4 text-sm text-ink-2 shadow-lg motion-safe:animate-fade-up"
    >
      <span className="tabular-nums">
        <span className="font-semibold text-ink">{actief + 1}/{treffers.length}</span> voor &ldquo;{q}&rdquo;
      </span>
      <button type="button" onClick={() => stap(-1)} className={knop} aria-label="Vorige treffer"><ChevronUp size={15} /></button>
      <button type="button" onClick={() => stap(1)} className={knop} aria-label="Volgende treffer"><ChevronDown size={15} /></button>
      <button type="button" onClick={sluit} className={knop} aria-label="Markering weghalen"><X size={15} /></button>
    </div>
  );
}
